import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Download, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DataTable,
  DateRangeFilter,
  ExportButtons,
  FilterBar,
  Pager,
  Pill,
  SearchInput,
  Section,
  SelectFilter,
  StatCard,
  StatusBadge,
  usePaged,
  withinRange,
} from "@/components/admin/admin-kit";
import {
  adminListBilling,
  adminListOrders,
  adminListWithdrawals,
  adminUpdateOrder,
  adminUpdateWithdrawal,
} from "@/lib/admin.functions";
import { admInvoicePdf, admInvoices, admResendInvoice } from "@/lib/admin-enterprise.functions";
import { formatMoney, formatUsd } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/finance")({
  head: () =>
    privatePageMetadata(
      "/nc/live/adm/finance",
      "Finance | Control room",
      "Internal administration.",
    ),
  component: AdminFinance,
});

const TABS = ["orders", "invoices", "payouts", "billing"] as const;
const ORDER_STATUSES = [
  "created",
  "pending_payment",
  "paid",
  "failed",
  "refunded",
  "cancelled",
] as const;
const PAYOUT_STATUSES = ["requested", "approved", "processing", "paid", "rejected"] as const;
const ORDER_KINDS = ["all", "topup", "boost", "listing", "listing_edit", "pro", "offer"] as const;

function AdminFinance() {
  const [tab, setTab] = useState<(typeof TABS)[number]>("orders");
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <Pill key={t} active={tab === t} onClick={() => setTab(t)}>
            {t}
          </Pill>
        ))}
      </div>
      {tab === "orders" && <OrdersTab />}
      {tab === "invoices" && <InvoicesTab />}
      {tab === "payouts" && <PayoutsTab />}
      {tab === "billing" && <BillingTab />}
    </div>
  );
}

/* ------------------------------- Orders -------------------------------- */

function OrdersTab() {
  const qc = useQueryClient();
  const list = useServerFn(adminListOrders);
  const setOrder = useServerFn(adminUpdateOrder);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [kind, setKind] = useState<string>("all");
  const [range, setRange] = useState({ from: "", to: "" });

  const { data = [], isLoading } = useQuery({
    queryKey: ["adm-orders"],
    queryFn: () => list({ data: {} }),
  });

  const mut = useMutation({
    mutationFn: (input: { orderId: string; status: (typeof ORDER_STATUSES)[number] }) =>
      setOrder({ data: input }),
    onSuccess: () => {
      toast.success("Order updated.");
      void qc.invalidateQueries({ queryKey: ["adm-orders"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return data.filter((o) => {
      if (status !== "all" && o.status !== status) return false;
      if (kind !== "all" && o.kind !== kind) return false;
      if (!withinRange(o.created_at, range.from, range.to)) return false;
      if (!s) return true;
      return [o.profile?.display_name, o.invoice_number, o.receipt_number, o.id, o.kind]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s));
    });
  }, [data, search, status, kind, range]);

  const exportRows = rows.map((o) => ({
    date: new Date(o.created_at).toISOString().slice(0, 10),
    user: o.profile?.display_name ?? o.user_id,
    kind: o.kind,
    status: o.status,
    aed: (Number(o.amount_due_cents) / 100).toFixed(2),
    paid_aed: (Number(o.amount_paid_cents ?? 0) / 100).toFixed(2),
    document: o.invoice_number ?? o.receipt_number ?? "",
    order_id: o.id,
  }));

  const paged = usePaged(rows, 50);
  const revenue = rows.reduce((s, o) => s + (o.status === "paid" ? Number(o.amount_paid_cents ?? 0) : 0), 0);

  return (
    <Section
      title="Orders"
      description="Every checkout, top-up and boost payment."
      actions={<ExportButtons rows={exportRows} filename="behot-orders" title="Orders" />}
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatCard label="Orders" value={rows.length} />
        <StatCard label="Collected" value={formatMoney(revenue)} hint={formatUsd(revenue)} tone="ok" />
        <StatCard label="Paid orders" value={rows.filter((o) => o.status === "paid").length} />
      </div>
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="User, invoice or order id" />
        <SelectFilter
          label="Status"
          value={status}
          onChange={setStatus}
          options={[{ value: "all", label: "All" }, ...ORDER_STATUSES.map((s) => ({ value: s, label: s }))]}
        />
        <SelectFilter
          label="Kind"
          value={kind}
          onChange={setKind}
          options={ORDER_KINDS.map((k) => ({ value: k, label: k }))}
        />
        <DateRangeFilter from={range.from} to={range.to} onChange={setRange} />
      </FilterBar>
      {isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <DataTable
            rows={paged.slice}
            rowKey={(r) => r.id}
            columns={[
              {
                key: "date",
                header: "Date",
                sortValue: (r) => r.created_at,
                cell: (r) => (
                  <span className="whitespace-nowrap">
                    {new Date(r.created_at).toLocaleDateString()}
                  </span>
                ),
              },
              {
                key: "user",
                header: "User",
                sortValue: (r) => r.profile?.display_name ?? "",
                cell: (r) => r.profile?.display_name ?? r.user_id.slice(0, 8),
              },
              { key: "kind", header: "Kind", sortValue: (r) => r.kind, cell: (r) => r.kind },
              {
                key: "amount",
                header: "Amount",
                sortValue: (r) => Number(r.amount_due_cents),
                cell: (r) => (
                  <span className="whitespace-nowrap">
                    {formatMoney(Number(r.amount_due_cents))}
                    <span className="ml-1 text-xs text-muted-foreground">
                      {formatUsd(Number(r.amount_due_cents))}
                    </span>
                  </span>
                ),
              },
              {
                key: "status",
                header: "Status",
                sortValue: (r) => r.status,
                cell: (r) => (
                  <select
                    className="rounded-md border bg-background px-2 py-1 text-xs"
                    value={r.status}
                    onChange={(e) =>
                      mut.mutate({
                        orderId: r.id,
                        status: e.target.value as (typeof ORDER_STATUSES)[number],
                      })
                    }
                  >
                    {ORDER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                ),
              },
              {
                key: "doc",
                header: "Document",
                cell: (r) => (
                  <span className="text-xs text-muted-foreground">
                    {r.invoice_number ?? r.receipt_number ?? "—"}
                  </span>
                ),
              },
            ]}
          />
          <Pager {...paged} onPrev={paged.prev} onNext={paged.next} />
        </>
      )}
    </Section>
  );
}

/* ------------------------------ Invoices ------------------------------- */

function InvoicesTab() {
  const list = useServerFn(admInvoices);
  const pdf = useServerFn(admInvoicePdf);
  const resend = useServerFn(admResendInvoice);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("all");
  const [docType, setDocType] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });

  const { data = [], isLoading } = useQuery({
    queryKey: ["adm-invoices", kind, range.from, range.to],
    queryFn: () =>
      list({
        data: {
          ...(kind !== "all" ? { kind } : {}),
          ...(range.from ? { from: range.from } : {}),
          ...(range.to ? { to: range.to } : {}),
        },
      }),
  });

  const download = useMutation({
    mutationFn: (orderId: string) => pdf({ data: { orderId } }),
    onSuccess: async (res) => {
      const { downloadBase64 } = await import("@/lib/admin/export");
      downloadBase64(res.base64, res.fileName);
      toast.success("Invoice downloaded.");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Download failed."),
  });

  const email = useMutation({
    mutationFn: (orderId: string) => resend({ data: { orderId } }),
    onSuccess: () => toast.success("Invoice emailed to the customer and finance."),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Send failed."),
  });

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return data.filter((r) => {
      if (docType !== "all" && r.doc_type !== docType) return false;
      if (!s) return true;
      return [r.number, r.user_name, r.billing_email, r.company_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s));
    });
  }, [data, search, docType]);

  const exportRows = rows.map((r) => ({
    number: r.number,
    type: r.doc_type,
    date: r.created_at.slice(0, 10),
    customer: r.user_name,
    company: r.company_name ?? "",
    email: r.billing_email ?? "",
    country: r.country_code ?? "",
    kind: r.kind,
    status: r.status,
    aed: (r.amount_due_cents / 100).toFixed(2),
    usd: (r.amount_due_cents / 100 / 4.33).toFixed(2),
  }));

  const paged = usePaged(rows, 50);
  const total = rows.reduce((s, r) => s + r.amount_due_cents, 0);

  return (
    <Section
      title="Invoices & receipts"
      description="Every issued document, downloadable as the branded BeHot.Now PDF."
      actions={<ExportButtons rows={exportRows} filename="behot-invoices" title="Invoices" />}
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatCard label="Documents" value={rows.length} />
        <StatCard label="Invoiced" value={formatMoney(total)} hint={formatUsd(total)} tone="hot" />
        <StatCard
          label="Invoices vs receipts"
          value={`${rows.filter((r) => r.doc_type === "invoice").length} / ${rows.filter((r) => r.doc_type === "receipt").length}`}
        />
      </div>
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Number, customer or email" />
        <SelectFilter
          label="Type"
          value={docType}
          onChange={setDocType}
          options={[
            { value: "all", label: "All" },
            { value: "invoice", label: "Invoices" },
            { value: "receipt", label: "Receipts" },
          ]}
        />
        <SelectFilter
          label="Kind"
          value={kind}
          onChange={setKind}
          options={ORDER_KINDS.map((k) => ({ value: k, label: k }))}
        />
        <DateRangeFilter from={range.from} to={range.to} onChange={setRange} />
      </FilterBar>
      {isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <DataTable
            rows={paged.slice}
            rowKey={(r) => r.order_id}
            empty="No invoices for these filters."
            columns={[
              {
                key: "number",
                header: "Number",
                sortValue: (r) => r.number,
                cell: (r) => <span className="font-mono text-xs">{r.number}</span>,
              },
              {
                key: "date",
                header: "Date",
                sortValue: (r) => r.created_at,
                cell: (r) => new Date(r.created_at).toLocaleDateString(),
              },
              {
                key: "customer",
                header: "Customer",
                sortValue: (r) => r.user_name,
                cell: (r) => (
                  <div>
                    <p>{r.user_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.company_name ?? r.billing_email ?? "—"}
                    </p>
                  </div>
                ),
              },
              { key: "kind", header: "Kind", sortValue: (r) => r.kind, cell: (r) => r.kind },
              {
                key: "amount",
                header: "Amount",
                sortValue: (r) => r.amount_due_cents,
                cell: (r) => (
                  <span className="whitespace-nowrap">
                    {formatMoney(r.amount_due_cents)}
                    <span className="ml-1 text-xs text-muted-foreground">
                      {formatUsd(r.amount_due_cents)}
                    </span>
                  </span>
                ),
              },
              {
                key: "status",
                header: "Status",
                sortValue: (r) => r.status,
                cell: (r) => <StatusBadge status={r.status} />,
              },
              {
                key: "actions",
                header: "",
                cell: (r) => (
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={download.isPending}
                      onClick={() => download.mutate(r.order_id)}
                    >
                      <Download className="mr-1 size-3.5" /> PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={email.isPending}
                      onClick={() => email.mutate(r.order_id)}
                    >
                      <Mail className="size-3.5" />
                    </Button>
                  </div>
                ),
              },
            ]}
          />
          <Pager {...paged} onPrev={paged.prev} onNext={paged.next} />
        </>
      )}
    </Section>
  );
}

/* ------------------------------- Payouts ------------------------------- */

type PayoutRowData = {
  id: string;
  amount_cents: number;
  currency: string;
  status: string;
  created_at: string;
  user_id: string;
  method: {
    method_type: string;
    account_holder: string | null;
    iban: string | null;
    bic_swift: string | null;
    crypto_asset: string | null;
    crypto_address: string | null;
  } | null;
};

function PayoutsTab() {
  const qc = useQueryClient();
  const list = useServerFn(adminListWithdrawals);
  const update = useServerFn(adminUpdateWithdrawal);
  const [status, setStatus] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });

  const { data = [], isLoading } = useQuery({
    queryKey: ["adm-payouts"],
    queryFn: () => list({ data: {} }),
  });

  const mut = useMutation({
    mutationFn: (input: {
      withdrawalId: string;
      status: (typeof PAYOUT_STATUSES)[number];
      reference?: string;
    }) => update({ data: input }),
    onSuccess: () => {
      toast.success("Payout updated.");
      void qc.invalidateQueries({ queryKey: ["adm-payouts"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const rows = (data as PayoutRowData[]).filter(
    (w) =>
      (status === "all" || w.status === status) && withinRange(w.created_at, range.from, range.to),
  );

  const exportRows = rows.map((w) => ({
    requested: w.created_at.slice(0, 10),
    status: w.status,
    aed: (Number(w.amount_cents) / 100).toFixed(2),
    method: w.method?.method_type ?? "",
    beneficiary: w.method?.account_holder ?? w.method?.crypto_address ?? "",
    iban: w.method?.iban ?? "",
    bic: w.method?.bic_swift ?? "",
    user_id: w.user_id,
  }));

  return (
    <Section
      title="Payout requests"
      description="48h verification, then 5–8 working days processing."
      actions={<ExportButtons rows={exportRows} filename="behot-payouts" title="Payouts" />}
    >
      <FilterBar>
        <SelectFilter
          label="Status"
          value={status}
          onChange={setStatus}
          options={[{ value: "all", label: "All" }, ...PAYOUT_STATUSES.map((s) => ({ value: s, label: s }))]}
        />
        <DateRangeFilter from={range.from} to={range.to} onChange={setRange} />
      </FilterBar>
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      <div className="space-y-2">
        {rows.length === 0 && !isLoading && (
          <p className="py-6 text-center text-sm text-muted-foreground">No payout requests.</p>
        )}
        {rows.map((w) => (
          <PayoutRow
            key={w.id}
            row={w}
            onUpdate={(s, reference) =>
              mut.mutate({ withdrawalId: w.id, status: s, ...(reference ? { reference } : {}) })
            }
          />
        ))}
      </div>
    </Section>
  );
}

function PayoutRow({
  row,
  onUpdate,
}: {
  row: PayoutRowData;
  onUpdate: (status: (typeof PAYOUT_STATUSES)[number], reference?: string) => void;
}) {
  const [reference, setReference] = useState("");
  return (
    <div className="card-surface p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            {formatMoney(Number(row.amount_cents))}{" "}
            <span className="text-xs text-muted-foreground">
              {formatUsd(Number(row.amount_cents))} · {row.currency}
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            {row.method?.method_type === "crypto"
              ? `${row.method.crypto_asset} ERC20 · ${row.method.crypto_address}`
              : `${row.method?.account_holder ?? "—"} · ${row.method?.iban ?? "—"} · ${row.method?.bic_swift ?? "—"}`}
          </p>
          <p className="text-xs text-muted-foreground">
            Requested {new Date(row.created_at).toLocaleString()} ·{" "}
            <StatusBadge status={row.status} />
          </p>
        </div>
        <Input
          placeholder="Payment reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          className="max-w-[200px]"
        />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => onUpdate("approved")}>
            Approve
          </Button>
          <Button size="sm" variant="hot" onClick={() => onUpdate("paid", reference)}>
            Mark paid
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onUpdate("rejected")}>
            Reject &amp; refund
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Billing ------------------------------- */

function BillingTab() {
  const list = useServerFn(adminListBilling);
  const [search, setSearch] = useState("");
  const { data = [], isLoading } = useQuery({
    queryKey: ["adm-billing"],
    queryFn: () => list({ data: {} }),
  });

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return data;
    return data.filter((b) =>
      [b.company_name, b.tax_id, b.billing_email, b.country_code]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s)),
    );
  }, [data, search]);

  const exportRows = rows.map((b) => ({
    company: b.company_name ?? "",
    tax_id: b.tax_id ?? "",
    email: b.billing_email ?? "",
    country: b.country_code ?? "",
    user_id: b.user_id,
  }));

  return (
    <Section
      title="Billing profiles"
      description="Company details used on invoices."
      actions={<ExportButtons rows={exportRows} filename="behot-billing" title="Billing profiles" />}
    >
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Company, tax id or email" />
      </FilterBar>
      {isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <DataTable
          rows={rows}
          rowKey={(r) => r.user_id}
          columns={[
            {
              key: "company",
              header: "Company",
              sortValue: (r) => r.company_name ?? "",
              cell: (r) => r.company_name ?? "—",
            },
            { key: "tax", header: "Tax ID", cell: (r) => r.tax_id ?? "—" },
            { key: "email", header: "Email", cell: (r) => r.billing_email ?? "—" },
            {
              key: "country",
              header: "Country",
              sortValue: (r) => r.country_code ?? "",
              cell: (r) => r.country_code ?? "—",
            },
          ]}
        />
      )}
    </Section>
  );
}
